import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import api from '../lib/api';
import toast from 'react-hot-toast';
import {
  ELECTRICAL_ASSET_TYPES,
  FITMENTS_ASSET_TYPES,
  ASSET_TYPE_DEFAULTS,
  ASSET_FREQUENCY_MAP,
  CONDITION_RATINGS,
  buildInitialFormData,
  buildAssetPayload,
  usesBoardCircuitFields,
} from '../lib/assetCreation';

/**
 * Modal for adding a new asset during a test session.
 * Site and element type are pre-filled and locked from session context.
 *
 * Props:
 *   siteId          {string|number} — locked, from session
 *   siteDisplayName {string}        — shown as read-only label
 *   elementType     {string}        — 'electrical' | 'fitments', locked from session
 *   sessionAssetType {string}       — session's active asset type, used to default the dropdown
 *   onSuccess       {function}      — called with new asset object after creation
 *   onClose         {function}      — called when modal is dismissed
 */
export default function AddAssetModal({
  siteId,
  siteDisplayName,
  elementType,
  sessionAssetType,
  onSuccess,
  onClose,
}) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(() =>
    buildInitialFormData(elementType, sessionAssetType)
  );

  const assetTypeOptions =
    elementType === 'fitments' ? FITMENTS_ASSET_TYPES : ELECTRICAL_ASSET_TYPES;

  const handleAssetTypeChange = (e) => {
    const type = e.target.value;
    const defaults = ASSET_TYPE_DEFAULTS[type] || {};
    setFormData((prev) => ({
      ...prev,
      assetType: type,
      testFrequencyMonths: ASSET_FREQUENCY_MAP[type] || 12,
      element: defaults.element || '',
      component: defaults.component || '',
      itemType: defaults.itemType || '',
    }));
  };

  const createMutation = useMutation(
    (data) => api.post('/assets', data),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(['sessionAssets', String(siteId)]);
        toast.success('Asset added to session');
        onSuccess(res.data);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to create asset'),
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(buildAssetPayload(formData, { siteId, elementType }));
  };

  const set = (field) => (e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const elementTypeLabel = elementType === 'fitments' ? 'Workshop Equipment' : 'Electrical';
  const isBoardOrRcd = usesBoardCircuitFields(formData.assetType);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Add Asset</h2>
        <form onSubmit={handleSubmit}>

          {/* Locked context — shown as read-only info, not inputs */}
          <div className="form-row">
            <div className="form-group">
              <label>Site</label>
              <input value={siteDisplayName} readOnly className="input-readonly" />
            </div>
            <div className="form-group">
              <label>Element Type</label>
              <input value={elementTypeLabel} readOnly className="input-readonly" />
            </div>
          </div>

          {/* Asset Type — dropdown filtered to element type */}
          <div className="form-group">
            <label>Asset Type *</label>
            <select value={formData.assetType} onChange={handleAssetTypeChange} required>
              {assetTypeOptions.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Location */}
          <div className="form-row">
            <div className="form-group">
              <label>Building *</label>
              <input
                value={formData.building}
                onChange={set('building')}
                required
                placeholder="e.g. B00, Block A"
              />
            </div>
            <div className="form-group">
              <label>Room *</label>
              <input
                value={formData.room}
                onChange={set('room')}
                required
                placeholder="e.g. R0001, HR0002"
              />
            </div>
          </div>

          {/* Board / circuit identifiers — boards and RCDs only */}
          {isBoardOrRcd && (
            <div className="form-row">
              <div className="form-group">
                <label>DB Number</label>
                <input
                  value={formData.dbNumber}
                  onChange={set('dbNumber')}
                  placeholder="e.g. DB13A"
                />
              </div>
              <div className="form-group">
                <label>Circuit Breaker Number</label>
                <input
                  value={formData.circuitBreakerNumber}
                  onChange={set('circuitBreakerNumber')}
                  placeholder="e.g. CB01 (leave blank for the board itself)"
                />
              </div>
            </div>
          )}

          {/* Equipment details */}
          <div className="form-row">
            {!isBoardOrRcd && (
              <div className="form-group">
                <label>Equipment Code</label>
                <input
                  value={formData.equipmentCode}
                  onChange={set('equipmentCode')}
                  placeholder="e.g. DB13A - CB01"
                />
              </div>
            )}
            <div className="form-group">
              <label>Manufacturer</label>
              <input value={formData.manufacturer} onChange={set('manufacturer')} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Model</label>
              <input value={formData.model} onChange={set('model')} />
            </div>
            <div className="form-group">
              <label>Serial Number</label>
              <input value={formData.serialNumber} onChange={set('serialNumber')} />
            </div>
          </div>

          {/* Dates & condition */}
          <div className="form-row">
            <div className="form-group">
              <label>Installation Date</label>
              <input type="date" value={formData.installationDate} onChange={set('installationDate')} />
            </div>
            <div className="form-group">
              <label>Test Frequency (months)</label>
              <input
                type="number"
                value={formData.testFrequencyMonths}
                readOnly
                className="input-readonly"
                title="Auto-set by asset type (Australian Standards)"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Condition</label>
              <select value={formData.conditionRating} onChange={set('conditionRating')}>
                <option value="">— Select condition —</option>
                {CONDITION_RATINGS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            {/* Capacity is board/circuit identity — buildAssetPayload drops it for
                other types, so do not offer an input that would be discarded. */}
            {isBoardOrRcd && (
              <div className="form-group">
                <label>Capacity</label>
                <input
                  value={formData.capacity}
                  onChange={set('capacity')}
                  placeholder="e.g. 16A, 50A"
                />
              </div>
            )}
          </div>

          {/* Item type — editable for fitments only */}
          {elementType === 'fitments' && (
            <div className="form-group">
              <label>Item / Type</label>
              <input
                value={formData.itemType}
                onChange={set('itemType')}
                placeholder="e.g. Bandsaw, Air Compressor, Pool Pump"
              />
            </div>
          )}

          <div className="form-group">
            <label>Notes</label>
            <textarea value={formData.notes} onChange={set('notes')} rows={3} />
          </div>

          <div className="form-actions">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={createMutation.isLoading}>
              {createMutation.isLoading ? 'Adding…' : 'Add Asset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
